import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { Button, Input } from "@/components/ui";
import { AUTH_CHALLENGES } from "@/constants/auth";
import { useAuth } from "@/context/auth/AuthContext";
import { useToast } from "@/hooks/useToast";
import { AuthPageLayout } from "@/layouts/AuthPageLayout";
const setPasswordSchema = z.object({
    newPassword: z.string().min(8, "Password must be at least 8 characters").regex(/[A-Z]/, "Password must contain at least one uppercase letter").regex(/[a-z]/, "Password must contain at least one lowercase letter").regex(/[0-9]/, "Password must contain at least one number").regex(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
    confirmPassword: z.string().min(1, "Please confirm your password")
}).refine((data)=>data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: [
        "confirmPassword"
    ]
});
export default function SetPasswordPage() {
    const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
        resolver: zodResolver(setPasswordSchema)
    });
    const { changePassword, challengeData, error: authError, mfaSetupRequired, clearError } = useAuth();
    const toast = useToast();
    const navigate = useNavigate();
    useEffect(()=>{
        // If no challenge data, redirect to login
        if (!challengeData) {
            navigate("/login");
            return;
        }
        // If MFA_SETUP challenge, redirect to MFA setup page
        if (challengeData.challengeName === AUTH_CHALLENGES.MFA_SETUP) {
            navigate("/mfa-setup");
            return;
        }
        // If org requires MFA setup after password change, redirect to setup
        if (mfaSetupRequired) {
            navigate("/mfa-setup");
            return;
        }
    }, [
        challengeData,
        mfaSetupRequired,
        navigate
    ]);
    useEffect(()=>{
        if (authError) {
            // Clear the error without showing toast - we'll handle it in onSubmit
            clearError();
        }
    }, [
        authError,
        clearError
    ]);
    const onSubmit = async (data)=>{
        if (!challengeData) {
            toast.error("Invalid session. Please log in again.");
            navigate("/login");
            return;
        }
        try {
            await changePassword({
                email: challengeData.email,
                newPassword: data.newPassword,
                session: challengeData.session
            });
        // Success - user will be redirected by AuthContext
        } catch (error) {
            toast.handleApiError(error, "Failed to set password");
        }
    };
    // Don't render if no challenge data
    if (!challengeData) {
        return null;
    }
    return /*#__PURE__*/ _jsx(AuthPageLayout, {
        title: "Set Your Password",
        subtitle: "Please set a permanent password for your account.",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "w-full",
            children: [
                /*#__PURE__*/ _jsxs("form", {
                    onSubmit: handleSubmit(onSubmit),
                    className: "w-full flex flex-col gap-4",
                    children: [
                        /*#__PURE__*/ _jsx(Input, {
                            label: "New Password",
                            type: "password",
                            placeholder: "Create a secure password",
                            ...register("newPassword"),
                            errors: errors.newPassword
                        }),
                        /*#__PURE__*/ _jsx(Input, {
                            label: "Confirm New Password",
                            type: "password",
                            placeholder: "Confirm your new password",
                            ...register("confirmPassword"),
                            errors: errors.confirmPassword
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            type: "submit",
                            variant: "simple",
                            color: "navy-90",
                            className: "w-full",
                            disabled: isSubmitting,
                            children: isSubmitting ? "Setting Password..." : "Set Password"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "mt-6 text-center",
                    children: /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-gray-500",
                        children: "Password must contain at least 8 characters with uppercase, lowercase, number, and special character."
                    })
                })
            ]
        })
    });
}
